/**
 * Centralized Supabase query layer
 * 
 * This module provides a clean, type-safe interface for all database operations.
 * It replaces DynamoDB with Supabase queries organized by entity type.
 * 
 * Structure:
 * - messages/: Message-specific queries
 * - chats/: Chat-specific queries
 * - chat-summaries/: Chat summary queries
 * - tool-results/: Tool result summary queries
 * - tool-approvals/: Tool approval queries
 * - types.ts: Shared types and interfaces
 */

// Export types
export type {
  QueryOptions,
  QueryResult,
  StoredMessage,
  StoredChat,
  StoredToolResultSummary,
  StoredToolApproval,
} from './types';

// Export message queries
export {
  getMessageById,
  getMessagesByChat,
  createMessage,
  createMessages,
  updateMessageParts,
  updateMessageMetadata,
  updateMessagePartsByMessageId,
  deleteMessage,
  deleteMessagesByChat,
} from './messages';

// Export chat queries
export {
  getChatById,
  getChatsByUser,
  getChatsByCreatedAfter,
  createChat,
  createChatWithId,
  updateChat,
  updateChatStatus,
  updateChatLastMessageAt,
  updateChatTitle,
  updateChatUserId,
  deleteChat,
} from './chats';

// Export chat summary queries
export {
  getChatSummaryById,
  getChatSummariesByUser,
  createChatSummary,
  updateChatSummary,
} from './chat-summaries';

// Export tool result queries
export {
  getToolResultById,
  getToolResultsByChat,
  getToolResultsByUser,
  getToolResultsByTool,
  createToolResult,
  updateToolResult,
  deleteToolResult,
} from './tool-results';

// Export tool approval queries
export {
  getApprovalById,
  getApprovalsByUser,
  getApprovalsByTool,
  getApprovalsByExpiration,
  createApproval,
  updateApproval,
  approveApproval,
  denyApproval,
  expireApproval,
  claimApprovalForExecution,
  recordApprovalExecutionResult,
  deleteApproval,
} from './tool-approvals';
export type { ClaimApprovalResult } from './tool-approvals';

// Export usage event queries
export {
  createUsageEvents,
  getChatUsageAggregate,
  getUsageTotalsByChat,
  type UsageEventInsert,
  type ChatUsageAggregate,
  type ChatUsageTotals,
} from './usage-events';

// Export tool IO-size queries
export { createToolIoEvent, type ToolIoEventInsert } from './tool-io';
